import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import SEO, { breadcrumbSchema } from "@/components/SEO";

const stanzas = [
  ["The top is not a place.", "It is a choice few are ready to make."],
  [
    "There is a lot of excellence in luxury.",
    "Beautiful stores, hotels, products, words.",
    "But beauty is not the problem.",
    "The problem is the gap.",
    "The gap between what a brand says and what a client feels.",
    "Between the strategy in the boardroom and the gesture on the floor.",
    "Between trained service and human connection.",
    "Between standards that exist and standards that live.",
  ],
  [
    "This is where exceptionality begins.",
    "Not with more polished gestures.",
    "With more truth and intention.",
    "Not with another concept.",
    "With the courage to build the culture, capability, and discipline behind the promise.",
  ],
  [
    "Next in Luxury was born for that work.",
    "We are a House of Impact for brands ready to go beyond excellence.",
    "We bring together luxury experts who have lived the ground, not only studied the market.",
    "People who understand the pressure of service, the complexity of teams,",
    "the fragility of consistency, and the invisible details that separate good from rare.",
  ],
  [
    "We build soul and system.",
    "Soul, so the brand has meaning.",
    "System, so meaning can be lived.",
    "Through strategy, training, experience design, and implementation, we turn ambition into behaviors, rituals, standards, and moments people can feel.",
    "We do not replace your teams.",
    "We build with them.",
    "Because exceptionality cannot be outsourced.",
    "It must be understood, practiced, protected, and passed on.",
  ],
  [
    "Excellent brands perform.",
    "Exceptional brands transform.",
    "The top is a choice.",
  ],
];

// Flatten all stanzas into a single ordered array of words with metadata
const flatWords = (() => {
  const out = [];
  stanzas.forEach((lines, si) => {
    lines.forEach((line, li) => {
      const words = line.split(" ");
      words.forEach((w, wi) => {
        out.push({
          word: w,
          stanzaIdx: si,
          lineIdx: li,
          wordIdx: wi,
          isLast: wi === words.length - 1,
        });
      });
    });
  });
  return out;
})();

const TOTAL_WORDS = flatWords.length;

export default function Manifesto() {
  const bodyRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = bodyRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // Start when top of element reaches 80% down the viewport.
        // Complete when bottom reaches 20% down the viewport.
        const start = vh * 0.8;
        const end = vh * 0.2;
        const totalDistance = rect.height + (start - end);
        const traveled = start - rect.top;
        const p = Math.max(0, Math.min(1, traveled / totalDistance));
        setProgress(p);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // How many words are "revealed" (dark) based on progress
  const revealedCount = Math.floor(progress * TOTAL_WORDS * 1.15);

  return (
    <div data-testid="page-manifesto" className="pt-32">
      <SEO
        title="Manifesto — Luxury Beyond Excellence"
        description="The Manifesto of Next in Luxury — a declaration on the discipline of the exceptional, the transmission of savoir-faire and the culture behind every truly rare luxury brand."
        path="/manifesto"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Manifesto", path: "/manifesto" },
        ])}
      />
      {/* Title */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2">
          <p className="font-eyebrow opacity-60">&nbsp;</p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <Reveal>
            <h1
              className="font-script leading-[0.88]"
              style={{ fontSize: "clamp(4rem, 14vw, 14rem)", color: "#14140F" }}
            >
              Manifesto.
            </h1>
          </Reveal>
        </div>
      </section>

      <div className="hairline max-w-[1440px] mx-auto" />

      {/* Body — scroll-linked word reveal (light grey → black) */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24">
        <div className="grid grid-cols-12 gap-6">
          <aside className="col-span-12 md:col-span-2 md:sticky md:top-32 md:self-start">
            <div className="hairline mb-4" style={{ width: "48px" }} />
            <p className="font-eyebrow opacity-50">The Manifesto</p>
          </aside>

          <div
            ref={bodyRef}
            className="col-span-12 md:col-span-8 md:col-start-4"
          >
            {(() => {
              let globalIdx = 0;
              return stanzas.map((lines, si) => (
                <div key={si} className="mb-12 md:mb-14">
                  {lines.map((line, li) => {
                    const words = line.split(" ");
                    return (
                      <p
                        key={li}
                        className="font-body"
                        style={{
                          fontSize: "clamp(1.05rem, 1.1vw, 1.2rem)",
                          lineHeight: 1.7,
                          maxWidth: "62ch",
                          fontFeatureSettings:
                            '"liga" 1, "onum" 1, "kern" 1',
                        }}
                        data-testid={`manifesto-line-${si}-${li}`}
                      >
                        {words.map((w, wi) => {
                          const isRevealed = globalIdx < revealedCount;
                          const idx = globalIdx;
                          globalIdx++;
                          return (
                            <span
                              key={wi}
                              style={{
                                color: isRevealed ? "#14140F" : "#C4BEB0",
                                opacity: isRevealed ? 0.92 : 1,
                                transition:
                                  "color 500ms cubic-bezier(0.2, 0.6, 0.2, 1)",
                                transitionDelay: `${Math.min(
                                  (idx - revealedCount + 10) * 20,
                                  200
                                )}ms`,
                              }}
                            >
                              {w}
                              {wi < words.length - 1 ? " " : ""}
                            </span>
                          );
                        })}
                      </p>
                    );
                  })}
                </div>
              ));
            })()}

            <Reveal delay={200}>
              <div className="mt-16">
                <p
                  className="font-script"
                  style={{
                    fontSize: "clamp(2.5rem, 6vw, 5rem)",
                    color: "#8C6B3F",
                    lineHeight: 1.1,
                    letterSpacing: 0,
                  }}
                >
                  Be the Next one
                </p>
                <p className="font-eyebrow mt-8 opacity-60">
                  — Signed, The House
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* End */}
    </div>
  );
}
